import React, { useState, useEffect } from 'react';
import { Info, Download, Smartphone, Monitor, Tablet } from 'lucide-react';

function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Prüfe ob App bereits installiert ist
    const checkInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches || 
          window.navigator.standalone ||
          document.referrer.includes('android-app://')) {
        setIsInstalled(true);
        return true;
      }
      return false;
    };

    if (checkInstalled()) {
      return;
    }

    // Browser-Install-Prompt Event (Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e) => {
      // Verhindere den Standard-Prompt und speichere das Event
      e.preventDefault();
      setDeferredPrompt(e);
      console.log('✅ Install-Prompt verfügbar');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Prüfe regelmäßig ob App installiert wurde
    const interval = setInterval(() => {
      if (checkInstalled()) {
        setIsInstalled(true);
        clearInterval(interval);
      }
    }, 1000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearInterval(interval);
    };
  }, []);

  const handleInstallClick = async () => {
    setIsInstalling(true);
    
    if (deferredPrompt) {
      // Chrome/Edge/Opera - Direkter Install-Prompt
      try {
        // Zeige den nativen Browser-Install-Prompt sofort
        deferredPrompt.prompt();
        
        // Warte auf User-Entscheidung
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`✅ Install-Prompt Ergebnis: ${outcome}`);
        
        // Cleanup
        setDeferredPrompt(null);
        
        if (outcome === 'accepted') {
          console.log('✅ App-Installation wurde akzeptiert');
          // Warte kurz und prüfe ob Installation erfolgreich war
          setTimeout(() => {
            if (window.matchMedia('(display-mode: standalone)').matches) {
              setIsInstalled(true);
            }
          }, 1000);
        } else {
          console.log('❌ App-Installation wurde abgelehnt');
        }
      } catch (error) {
        console.error('❌ Fehler beim Anzeigen des Install-Prompts:', error);
        // Fallback: Zeige Anweisungen
        setShowInstructions(true);
      }
    } else {
      // iOS Safari oder andere Browser ohne nativen Prompt
      // Zeige Anweisungen sofort an
      setShowInstructions(true);
    }
    
    setIsInstalling(false);
  };

  const getDeviceInstructions = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isAndroid = /Android/.test(navigator.userAgent);

    if (isIOS) {
      return {
        title: 'App auf iPhone/iPad installieren',
        steps: [
          'Tippe auf das Teilen-Symbol (Quadrat mit Pfeil nach oben) am unteren Rand',
          'Scrolle nach unten und wähle "Zum Home-Bildschirm"',
          'Tippe oben rechts auf "Hinzufügen"',
          'Die App erscheint jetzt auf deinem Home-Bildschirm!'
        ],
        Icon: Smartphone
      };
    } else if (isAndroid) {
      return {
        title: 'App auf Android installieren',
        steps: [
          'Öffne das Menü (drei Punkte oben rechts im Browser)',
          'Wähle "Zur Startseite hinzufügen" oder "App installieren"',
          'Bestätige die Installation',
          'Die App erscheint jetzt auf deinem Home-Bildschirm!'
        ],
        Icon: Smartphone
      };
    } else {
      return {
        title: 'App auf Desktop installieren',
        steps: [
          'Suche nach dem Installations-Symbol (➕) in der Adressleiste',
          'Oder verwende das Menü deines Browsers',
          'Wähle "App installieren" oder "Zur Startseite hinzufügen"',
          'Die App öffnet sich jetzt wie eine Desktop-App!'
        ],
        Icon: Monitor
      };
    }
  };

  // Verstecke Button wenn bereits installiert
  if (isInstalled) {
    return null;
  }

  const instructions = getDeviceInstructions();

  return (
    <>

      {/* Installations-Anleitung Modal */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-800 border border-zinc-700 rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in">
            <button
              onClick={() => setShowInstructions(false)}
              className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-200 transition-colors p-1"
              aria-label="Schließen"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-gradient-to-br from-zinc-700 to-zinc-600 rounded-2xl border border-zinc-500/50">
                  {React.createElement(instructions.Icon, { 
                    className: "w-12 h-12 text-zinc-100", 
                    strokeWidth: 2 
                  })}
                </div>
              </div>
              <h3 className="text-2xl font-bold text-zinc-100 mb-2">
                App installieren
              </h3>
              <p className="text-zinc-400 text-sm">
                {instructions.title}
              </p>
            </div>

            <div className="bg-zinc-900/50 border border-zinc-700/50 rounded-lg p-4 mb-4 space-y-3">
              {instructions.steps.map((step, index) => (
                <div key={index} className="text-zinc-300 text-sm flex items-start">
                  <span className="text-zinc-500 mr-3 font-bold flex-shrink-0">{index + 1}.</span>
                  <span className="text-left">{step}</span>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowInstructions(false)}
                className="flex-1 bg-gradient-to-r from-zinc-700 to-zinc-600 hover:from-zinc-600 hover:to-zinc-500 text-zinc-100 font-medium py-3 px-6 rounded-lg transition-all duration-200"
              >
                Verstanden
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default InstallPWA;
